import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import StatusBadge from '../components/StatusBadge';
import {
  BookOpen, FileText, Users, Shield, Zap, BarChart2, Plus, Trash2,
  AlertCircle, X, UserPlus, Eye, EyeOff, Edit2, Save, ChevronDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const ROLE_CONFIG = {
  author: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Author' },
  reviewer: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', label: 'Reviewer' },
  admin: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Admin / Editor' },
  superadmin: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Super Admin' },
};

const STATUS_OPTIONS = ['submitted', 'under_review', 'accepted', 'rejected', 'revision_requested'];

const SuperAdminDashboard = () => {
  const { user, token } = useAuth();
  const { refreshJournals, refreshBoardMembers } = useData();
  const [activeTab, setActiveTab] = useState('overview');

  // User Management State
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');

  // Journal State
  const [journals, setJournals] = useState([]);
  const [journalsLoading, setJournalsLoading] = useState(false);
  const [journalsError, setJournalsError] = useState('');
  const [showJournalModal, setShowJournalModal] = useState(null);
  const [showDeleteJournalModal, setShowDeleteJournalModal] = useState(null);

  // Submission State
  const [submissions, setSubmissions] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState('');
  const [showSubModal, setShowSubModal] = useState(null);
  const [showDeleteSubModal, setShowDeleteSubModal] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(null);

  // ✅ Zenodo Pending Count
  const [zenodoPending, setZenodoPending] = useState(0);
  const [zenodoRetrying, setZenodoRetrying] = useState(false);
  const [zenodoRetryMsg, setZenodoRetryMsg] = useState('');

  const tabs = ['overview', 'user management', 'journals', 'research papers', 'system'];

  // ========== FETCH FUNCTIONS ==========
  const fetchUsers = useCallback(async () => {
    if (!token) return;
    setUsersLoading(true);
    setUsersError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      setUsers(await res.json());
    } catch (err) { setUsersError(err.message); }
    finally { setUsersLoading(false); }
  }, [token]);

  const fetchJournals = useCallback(async () => {
    setJournalsLoading(true);
    setJournalsError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/journals`);
      if (!res.ok) throw new Error('Failed to fetch journals');
      setJournals(await res.json());
    } catch (err) { setJournalsError(err.message); }
    finally { setJournalsLoading(false); }
  }, []);

  const fetchSubmissions = useCallback(async () => {
    if (!token) return;
    setSubsLoading(true);
    setSubsError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/submissions`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch submissions');
      setSubmissions(await res.json());
    } catch (err) { setSubsError(err.message); }
    finally { setSubsLoading(false); }
  }, [token]);

  // ✅ Fetch Zenodo pending count
  const fetchZenodoPending = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/zenodo-pending-count`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setZenodoPending(data.pending || 0);
      }
    } catch {}
  }, [token]);

  // Fetch data on tab change
  useEffect(() => {
    if (activeTab === 'user management') fetchUsers();
    if (activeTab === 'journals' || activeTab === 'overview') fetchJournals();
    if (activeTab === 'research papers' || activeTab === 'overview') fetchSubmissions();
  }, [activeTab, fetchUsers, fetchJournals, fetchSubmissions]);

  // Initial load
  useEffect(() => {
    fetchUsers();
    fetchJournals();
    fetchSubmissions();
    fetchZenodoPending();
  }, [fetchUsers, fetchJournals, fetchSubmissions, fetchZenodoPending]);

  // ✅ Manual Zenodo retry
  const handleZenodoRetry = async () => {
    setZenodoRetrying(true);
    setZenodoRetryMsg('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/zenodo-retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      setZenodoRetryMsg(data.message || 'Retry triggered.');
      setTimeout(() => { fetchZenodoPending(); }, 4000);
    } catch {
      setZenodoRetryMsg('Failed to trigger retry. Check Render logs.');
    } finally {
      setZenodoRetrying(false);
    }
  };

  // ========== DELETE HANDLERS ==========
  const handleDeleteUser = async (userId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${userId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      setShowDeleteUserModal(null);
      fetchUsers();
      refreshBoardMembers();
    } catch (err) { alert(err.message); }
  };

  const handleDeleteJournal = async (journalId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/journals/${journalId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      setShowDeleteJournalModal(null);
      fetchJournals();
      refreshJournals();
    } catch (err) { alert(err.message); }
  };

  const handleDeleteSub = async (subId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/submissions/${subId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      setShowDeleteSubModal(null);
      fetchSubmissions();
    } catch (err) { alert(err.message); }
  };

  // ========== COMPUTED ==========
  const filteredUsers = roleFilter === 'all' ? users : users.filter(u => u.role === roleFilter);
  const userCounts = {
    all: users.length,
    admin: users.filter(u => u.role === 'admin').length,
    reviewer: users.filter(u => u.role === 'reviewer').length,
    author: users.filter(u => u.role === 'author').length,
  };

  return (
    <div className="bg-[#F5F7FA] min-h-screen" data-testid="superadmin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-2 mb-1">
            <p className="section-overline">Super Admin Portal</p>
            <span className="text-xs px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 font-medium">Full Access</span>
          </div>
          <h1 className="font-serif text-3xl font-semibold text-[#1F1F1F]">
            System Overview — {user?.name}
          </h1>
          <p className="text-[#6B7280] text-sm mt-1">Director General · MAH Quantum Research Institute</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {[
            { label: 'Total Users', value: users.length || '—', icon: <Users size={16} /> },
            { label: 'Journals', value: journals.length || '—', icon: <BookOpen size={16} /> },
            { label: 'Papers', value: submissions.length || '—', icon: <FileText size={16} /> },
            { label: 'Submitted', value: submissions.filter(s => s.status === 'submitted').length, icon: <BarChart2 size={16} /> },
            { label: 'Under Review', value: submissions.filter(s => ['editor_review', 'reviewer_review'].includes(s.status)).length, icon: <FileText size={16} /> },
            { label: 'Published', value: submissions.filter(s => s.status === 'published').length, icon: <Shield size={16} /> },
          ].map(stat => (
            <div key={stat.label} className="bg-white border border-[#E5E7EB] p-4" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
              <p className="text-2xl font-serif font-bold text-[#1F1F1F] mb-1">{stat.value}</p>
              <p className="text-xs text-[#6B7280]">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* ✅ Zenodo Pending Warning Banner */}
        {zenodoPending > 0 && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            <div className="flex items-start gap-3">
              <span className="text-xl mt-0.5">⚠️</span>
              <div className="flex-1">
                <p className="font-semibold">{zenodoPending} paper{zenodoPending > 1 ? 's' : ''} pending Zenodo upload</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  Zenodo was unavailable during approval. Files are safely stored in Supabase.
                </p>
                {zenodoRetryMsg && (
                  <p className="text-xs mt-1 font-medium text-amber-700">{zenodoRetryMsg}</p>
                )}
              </div>
              <button
                onClick={handleZenodoRetry}
                disabled={zenodoRetrying}
                className="shrink-0 px-3 py-1.5 bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {zenodoRetrying ? 'Retrying…' : 'Retry Now'}
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white border border-[#E5E7EB]">
          <div className="flex border-b border-[#E5E7EB] overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-3 text-sm font-medium capitalize whitespace-nowrap transition-colors border-b-2 ${
                  activeTab === tab ? 'border-[#1A73E8] text-[#1A73E8]' : 'border-transparent text-[#6B7280] hover:text-[#374151]'
                }`}
                data-testid={`superadmin-tab-${tab.replace(/\s/g, '-')}`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* ========== OVERVIEW TAB ========== */}
          {activeTab === 'overview' && (
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="overview-tab">
              <div className="border border-[#E5E7EB]">
                <div className="px-4 py-3 bg-[#F5F7FA] border-b border-[#E5E7EB]">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Recent Research Papers</h3>
                </div>
                <div className="divide-y divide-[#E5E7EB]">
                  {submissions.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#9CA3AF]">No research papers yet. Add papers from the Research Papers tab.</div>
                  ) : submissions.slice(0, 5).map(sub => (
                    <div key={sub.id} className="px-4 py-3 flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1F1F1F] truncate">{sub.title}</p>
                        <p className="text-xs text-[#9CA3AF]">{sub.authorName} · {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString() : '—'}</p>
                      </div>
                      <StatusBadge status={sub.status} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="border border-[#E5E7EB]">
                <div className="px-4 py-3 bg-[#F5F7FA] border-b border-[#E5E7EB]">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Journal Portfolio</h3>
                </div>
                <div className="divide-y divide-[#E5E7EB]">
                  {journals.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#9CA3AF]">No journals yet. Add journals from the Journals tab.</div>
                  ) : journals.slice(0, 5).map(j => (
                    <div key={j.id} className="px-4 py-3 flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1F1F1F]">{j.shortTitle}</p>
                        <p className="text-xs text-[#9CA3AF] truncate">{j.title}</p>
                      </div>
                      <span className="text-xs text-[#6B7280] whitespace-nowrap">{j.field}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ========== USER MANAGEMENT TAB ========== */}
          {activeTab === 'user management' && (
            <div data-testid="user-management-tab">
              <div className="px-5 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-[#374151]">User Management</h3>
                  <p className="text-xs text-[#9CA3AF] mt-0.5">Create and manage Admin/Editor, Reviewer, and Author accounts</p>
                </div>
                <button onClick={() => setShowCreateUserModal(true)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#1A73E8] text-white text-xs font-semibold hover:bg-[#174EA6] transition-colors"
                  data-testid="create-user-button">
                  <UserPlus size={14} /> Create New User
                </button>
              </div>
              <div className="px-5 py-3 border-b border-[#E5E7EB] flex gap-2 flex-wrap">
                {[{ key: 'all', label: 'All Users' }, { key: 'admin', label: 'Admin / Editor' }, { key: 'reviewer', label: 'Reviewers' }, { key: 'author', label: 'Authors' }].map(f => (
                  <button key={f.key} onClick={() => setRoleFilter(f.key)}
                    className={`px-3 py-1.5 text-xs font-medium border transition-colors ${roleFilter === f.key ? 'bg-[#1A73E8] text-white border-[#1A73E8]' : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:bg-[#F5F7FA]'}`}
                    data-testid={`filter-${f.key}`}>
                    {f.label} ({f.key === 'all' ? userCounts.all : userCounts[f.key] || 0})
                  </button>
                ))}
              </div>
              {usersError && <div className="mx-5 mt-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={14} /> {usersError}</div>}
              {usersLoading ? <LoadingSpinner text="Loading users..." /> : filteredUsers.length === 0 ? (
                <EmptyState icon={<Users size={32} />} text="No users found" action={() => setShowCreateUserModal(true)} actionLabel="+ Create the first user" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="users-table">
                    <thead><tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                      <Th>User</Th><Th>Email</Th><Th>Role</Th><Th>Affiliation</Th><Th>Created</Th><Th>Actions</Th>
                    </tr></thead>
                    <tbody className="divide-y divide-[#E5E7EB]">
                      {filteredUsers.map(u => {
                        const rs = ROLE_CONFIG[u.role] || ROLE_CONFIG.author;
                        return (
                          <tr key={u.id} className="hover:bg-[#F9FAFB]" data-testid={`user-row-${u.id}`}>
                            <td className="px-5 py-3"><div className="flex items-center gap-2.5">
                              <div className={`w-8 h-8 flex items-center justify-center ${u.role === 'superadmin' ? 'bg-red-600' : 'bg-[#1A73E8]'}`}>
                                <span className="text-white text-xs font-bold">{u.name.charAt(0).toUpperCase()}</span>
                              </div>
                              <div><p className="text-sm font-medium text-[#1F1F1F]">{u.name}</p>{u.designation && <p className="text-xs text-[#9CA3AF]">{u.designation}</p>}</div>
                            </div></td>
                            <td className="px-5 py-3 text-sm text-[#6B7280]">{u.email}</td>
                            <td className="px-5 py-3"><span className={`text-xs px-2.5 py-1 border font-medium ${rs.bg} ${rs.text} ${rs.border}`}>{rs.label}</span></td>
                            <td className="px-5 py-3 text-sm text-[#6B7280] max-w-[180px] truncate">{u.affiliation || '—'}</td>
                            <td className="px-5 py-3 text-xs text-[#9CA3AF]">{formatDate(u.created_at)}</td>
                            <td className="px-5 py-3">{u.role === 'superadmin' ? <span className="text-xs text-[#D1D5DB] italic">Protected</span> : (
                              <button onClick={() => setShowDeleteUserModal(u)} className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                                <Trash2 size={12} /> Delete
                              </button>
                            )}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ========== JOURNALS TAB ========== */}
          {activeTab === 'journals' && (
            <div data-testid="journals-tab">
              <div className="px-5 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-[#374151]">Journal Management</h3>
                  <p className="text-xs text-[#9CA3AF] mt-0.5">Add, edit, or remove journals</p>
                </div>
                <button onClick={() => setShowJournalModal('create')}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#1A73E8] text-white text-xs font-semibold hover:bg-[#174EA6] transition-colors"
                  data-testid="create-journal-button">
                  <Plus size={14} /> Add New Journal
                </button>
              </div>
              {journalsError && <div className="mx-5 mt-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={14} /> {journalsError}</div>}
              {journalsLoading ? <LoadingSpinner text="Loading journals..." /> : journals.length === 0 ? (
                <EmptyState icon={<BookOpen size={32} />} text="No journals yet" action={() => setShowJournalModal('create')} actionLabel="+ Add the first journal" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="journals-table">
                    <thead><tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                      <Th>Journal</Th><Th>Field</Th><Th>Frequency</Th><Th>ISSN</Th><Th>Impact Factor</Th><Th>Editors</Th><Th>Created</Th><Th>Actions</Th>
                    </tr></thead>
                    <tbody className="divide-y divide-[#E5E7EB]">
                      {journals.map(j => (
                        <tr key={j.id} className="hover:bg-[#F9FAFB]" data-testid={`journal-row-${j.id}`}>
                          <td className="px-5 py-3">
                            <p className="text-sm font-semibold text-[#1F1F1F]">{j.shortTitle}</p>
                            <p className="text-xs text-[#9CA3AF] max-w-[200px] truncate">{j.title}</p>
                          </td>
                          <td className="px-5 py-3 text-xs text-[#6B7280] max-w-[120px] truncate">{j.field}</td>
                          <td className="px-5 py-3 text-xs text-[#6B7280]">{j.frequency}</td>
                          <td className="px-5 py-3 font-mono text-xs text-[#6B7280]">{j.issn}</td>
                          <td className="px-5 py-3 text-sm font-semibold text-[#1A73E8]">{j.impactFactor}</td>
                          <td className="px-5 py-3">
                            {j.assignedEditorNames && j.assignedEditorNames.length > 0 ? (
                              <div className="space-y-0.5">
                                {j.assignedEditorNames.map((name, i) => (
                                  <p key={i} className="text-xs text-[#374151]">{name}</p>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-[#D1D5DB]">None</span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-xs text-[#9CA3AF]">{formatDate(j.created_at)}</td>
                          <td className="px-5 py-3">
                            <div className="flex gap-1">
                              <button onClick={() => setShowJournalModal(j)} className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-[#1A73E8] text-[#1A73E8] hover:bg-blue-50" data-testid={`edit-journal-${j.id}`}>
                                <Edit2 size={11} /> Edit
                              </button>
                              <button onClick={() => setShowDeleteJournalModal(j)} className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-journal-${j.id}`}>
                                <Trash2 size={11} /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ========== RESEARCH PAPERS TAB ========== */}
          {activeTab === 'research papers' && (
            <div data-testid="papers-tab">
              <div className="px-5 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-[#374151]">Research Papers</h3>
                  <p className="text-xs text-[#9CA3AF] mt-0.5">Add, edit, or remove research paper submissions</p>
                </div>
                <button onClick={() => setShowSubModal('create')}
                  className="flex items-center gap-1.5 px-4 py-2 bg-[#1A73E8] text-white text-xs font-semibold hover:bg-[#174EA6] transition-colors"
                  data-testid="create-paper-button">
                  <Plus size={14} /> Add New Paper
                </button>
              </div>
              {subsError && <div className="mx-5 mt-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={14} /> {subsError}</div>}
              {subsLoading ? <LoadingSpinner text="Loading papers..." /> : submissions.length === 0 ? (
                <EmptyState icon={<FileText size={32} />} text="No research papers yet" action={() => setShowSubModal('create')} actionLabel="+ Add the first paper" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="papers-table">
                    <thead><tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                      <Th>Title</Th><Th>Author</Th><Th>Journal</Th><Th>Status</Th><Th>Editors</Th><Th>Actions</Th>
                    </tr></thead>
                    <tbody className="divide-y divide-[#E5E7EB]">
                      {submissions.map(s => (
                        <tr key={s.id} className="hover:bg-[#F9FAFB]" data-testid={`paper-row-${s.id}`}>
                          {/* ✅ Change 2: Title cell with cover letter expander */}
                          <td className="px-5 py-3 max-w-[220px]">
                            <p className="text-sm font-medium text-[#1F1F1F] line-clamp-2">{s.title}</p>
                            {s.keywords && <p className="text-xs text-[#9CA3AF] mt-0.5 truncate">{s.keywords}</p>}
                            {s.zenodoPending && (
                              <span className="inline-block mt-1 text-xs px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">
                                ⏳ Zenodo pending
                              </span>
                            )}
                            {s.coverLetter && (
                              <details className="mt-1">
                                <summary className="text-xs text-[#1A73E8] cursor-pointer hover:underline select-none">
                                  📄 View Cover Letter
                                </summary>
                                <div className="mt-2 p-2 bg-blue-50 border border-blue-100 text-xs text-[#374151] leading-relaxed max-w-xs">
                                  {s.coverLetter}
                                </div>
                              </details>
                            )}
                          </td>
                          <td className="px-5 py-3 text-sm text-[#374151]">{s.authorName}</td>
                          <td className="px-5 py-3 text-xs text-[#6B7280]">{s.journalTitle || '—'}</td>
                          <td className="px-5 py-3">
                            <StatusBadge status={s.status} />
                            {s.editorDecisionByName && <p className="text-xs text-[#6B7280] mt-1">by {s.editorDecisionByName}</p>}
                            {s.reviewerApprovalByName && <p className="text-xs text-green-600 mt-0.5">Published by {s.reviewerApprovalByName}</p>}
                          </td>
                          {/* ✅ Change 1: Editors column with green/red/grey per-decision badges */}
                          <td className="px-5 py-3">
                            {s.assignedEditorNames && s.assignedEditorNames.length > 0 ? (
                              <div className="space-y-1">
                                {s.assignedEditorNames.map((name, i) => {
                                  const editorId = s.assignedEditors?.[i];
                                  const decision = s.editorDecisions?.[editorId];
                                  return (
                                    <div key={i} className={`flex items-center gap-1.5 text-xs px-2 py-0.5 border font-medium w-fit ${
                                      decision === 'approved'
                                        ? 'bg-green-50 text-green-700 border-green-200'
                                        : decision === 'rejected'
                                        ? 'bg-red-50 text-red-700 border-red-200'
                                        : 'bg-[#F5F7FA] text-[#9CA3AF] border-[#E5E7EB]'
                                    }`}>
                                      <span>{decision === 'approved' ? '✓' : decision === 'rejected' ? '✕' : '○'}</span>
                                      {name}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-xs text-[#D1D5DB]">None</span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex gap-1 flex-wrap">
                              {s.status === 'submitted' && (
                                <button onClick={() => setShowAssignModal(s)} className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-purple-300 text-purple-700 bg-purple-50 hover:bg-purple-100 font-medium" data-testid={`assign-editors-${s.id}`}>
                                  <Users size={11} /> Assign Editors
                                </button>
                              )}
                              {s.status === 'editor_review' && (
                                <button onClick={() => setShowAssignModal(s)} className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-purple-300 text-purple-700 hover:bg-purple-50" data-testid={`reassign-editors-${s.id}`}>
                                  <Users size={11} /> Reassign
                                </button>
                              )}
                              <button onClick={() => setShowSubModal(s)} className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-[#1A73E8] text-[#1A73E8] hover:bg-blue-50" data-testid={`edit-paper-${s.id}`}>
                                <Edit2 size={11} /> Edit
                              </button>
                              <button onClick={() => setShowDeleteSubModal(s)} className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-paper-${s.id}`}>
                                <Trash2 size={11} /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ========== SYSTEM TAB ========== */}
          {activeTab === 'system' && (
            <div className="p-6" data-testid="system-tab">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="border border-[#E5E7EB] p-5">
                  <h3 className="font-semibold text-[#1F1F1F] text-sm mb-4 pb-2 border-b border-[#E5E7EB]">Platform Configuration</h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Platform Name', value: 'MAH Quantum Research Institute' },
                      { label: 'Platform Version', value: '2.1.0' },
                      { label: 'Environment', value: 'Production' },
                      { label: 'Review Mode', value: 'Double-Blind' },
                      { label: 'Open Access', value: 'Hybrid' },
                    ].map(item => (
                      <div key={item.label} className="flex justify-between text-sm border-b border-[#F5F7FA] pb-2 last:border-0">
                        <span className="text-[#6B7280]">{item.label}</span>
                        <span className="font-medium text-[#1F1F1F]">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border border-[#E5E7EB]">
                  <div className="p-5 pb-0"><h3 className="font-semibold text-[#1F1F1F] text-sm mb-3">AI Features Roadmap</h3></div>
                  <div className="space-y-3 p-5">
                    {[
                      { title: 'AI Abstract Summarizer', status: 'Q2 2025', pct: 15 },
                      { title: 'AI Reviewer Matching', status: 'Q3 2025', pct: 8 },
                      { title: 'AI Paper Quality Check', status: 'Q4 2025', pct: 5 },
                    ].map(ai => (
                      <div key={ai.title} className="border border-[#E5E7EB] p-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-[#374151]">{ai.title}</p>
                          <div className="flex items-center gap-1.5"><Zap size={10} className="text-[#1A73E8]" /><span className="text-xs text-[#6B7280]">{ai.status}</span></div>
                        </div>
                        <div className="h-1.5 bg-[#E5E7EB]"><div className="h-1.5 bg-[#1A73E8]" style={{ width: `${ai.pct}%` }} /></div>
                        <p className="text-xs text-[#9CA3AF] mt-1">{ai.pct}% development progress</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========== MODALS ========== */}
      {showCreateUserModal && (
        <CreateUserModal token={token} onClose={() => setShowCreateUserModal(false)} onCreated={() => { setShowCreateUserModal(false); fetchUsers(); refreshBoardMembers(); }} />
      )}
      {showDeleteUserModal && (
        <DeleteConfirmModal item={showDeleteUserModal} itemType="user"
          onConfirm={() => handleDeleteUser(showDeleteUserModal.id)}
          onCancel={() => setShowDeleteUserModal(null)}
          description={<><strong>{showDeleteUserModal.name}</strong> ({showDeleteUserModal.email}) — {ROLE_CONFIG[showDeleteUserModal.role]?.label}</>}
        />
      )}
      {showJournalModal && (
        <JournalModal token={token}
          journal={showJournalModal === 'create' ? null : showJournalModal}
          onClose={() => setShowJournalModal(null)}
          onSaved={() => { setShowJournalModal(null); fetchJournals(); refreshJournals(); }}
        />
      )}
      {showDeleteJournalModal && (
        <DeleteConfirmModal item={showDeleteJournalModal} itemType="journal"
          onConfirm={() => handleDeleteJournal(showDeleteJournalModal.id)}
          onCancel={() => setShowDeleteJournalModal(null)}
          description={<><strong>{showDeleteJournalModal.shortTitle}</strong> — {showDeleteJournalModal.title}</>}
        />
      )}
      {showSubModal && (
        <SubmissionModal token={token} journals={journals}
          submission={showSubModal === 'create' ? null : showSubModal}
          onClose={() => setShowSubModal(null)}
          onSaved={() => { setShowSubModal(null); fetchSubmissions(); }}
        />
      )}
      {showDeleteSubModal && (
        <DeleteConfirmModal item={showDeleteSubModal} itemType="research paper"
          onConfirm={() => handleDeleteSub(showDeleteSubModal.id)}
          onCancel={() => setShowDeleteSubModal(null)}
          description={<><strong>{showDeleteSubModal.title}</strong> by {showDeleteSubModal.authorName}</>}
        />
      )}
      {showAssignModal && (
        <AssignEditorsModal
          token={token}
          submission={showAssignModal}
          editors={users.filter(u => u.role === 'admin')}
          onClose={() => setShowAssignModal(null)}
          onSaved={() => { setShowAssignModal(null); fetchSubmissions(); }}
        />
      )}
    </div>
  );
};

// =============================================
// REUSABLE COMPONENTS
// =============================================
const Th = ({ children }) => (
  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#6B7280]">{children}</th>
);

const LoadingSpinner = ({ text }) => (
  <div className="p-12 text-center">
    <div className="w-6 h-6 border-2 border-[#1A73E8] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
    <p className="text-sm text-[#9CA3AF]">{text}</p>
  </div>
);

const EmptyState = ({ icon, text, action, actionLabel }) => (
  <div className="p-12 text-center">
    <div className="mx-auto text-[#D1D5DB] mb-3">{icon}</div>
    <p className="text-sm text-[#9CA3AF]">{text}</p>
    {action && <button onClick={action} className="mt-3 text-xs text-[#1A73E8] hover:text-[#174EA6] font-medium">{actionLabel}</button>}
  </div>
);

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
};

// =============================================
// DELETE CONFIRM MODAL
// =============================================
const DeleteConfirmModal = ({ item, itemType, description, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid={`delete-${itemType}-modal`}>
    <div className="bg-white border border-[#E5E7EB] w-full max-w-md">
      <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
        <h3 className="font-serif text-lg font-semibold text-[#1F1F1F]">Confirm Deletion</h3>
        <button onClick={onCancel} className="text-[#9CA3AF] hover:text-[#374151]"><X size={18} /></button>
      </div>
      <div className="p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 bg-red-50 flex items-center justify-center flex-shrink-0"><Trash2 size={18} className="text-red-600" /></div>
          <div>
            <p className="text-sm font-medium text-[#1F1F1F] mb-1">Are you sure you want to delete this {itemType}?</p>
            <p className="text-sm text-[#6B7280]">{description}</p>
            <p className="text-xs text-[#9CA3AF] mt-1">This action cannot be undone.</p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-[#E5E7EB] text-[#374151] hover:bg-[#F5F7FA]">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-button">Delete</button>
        </div>
      </div>
    </div>
  </div>
);

// =============================================
// CREATE USER MODAL
// =============================================
const CreateUserModal = ({ token, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'author', affiliation: '', designation: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { setError('Name, email, and password are required.'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/users`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      onCreated();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="create-user-modal">
      <div className="bg-white border border-[#E5E7EB] w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div><h3 className="font-serif text-lg font-semibold text-[#1F1F1F]">Create New User</h3><p className="text-xs text-[#9CA3AF]">Add a new Admin/Editor, Reviewer, or Author</p></div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151]"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={14} /> {error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Full Name *" name="name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Dr. John Smith" testid="create-user-name" />
            <FormField label="Email *" name="email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="user@institution.edu" testid="create-user-email" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">Password *</label>
              <div className="relative">
                <input name="password" type={showPass ? 'text' : 'password'} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Min 6 characters" className="w-full px-3 py-2 pr-9 border border-[#E5E7EB] text-sm focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8] outline-none" data-testid="create-user-password" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]">
                  {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">Role *</label>
              <select name="role" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                className="w-full px-3 py-2 border border-[#E5E7EB] text-sm bg-white focus:ring-1 focus:ring-[#1A73E8] outline-none" data-testid="create-user-role">
                <option value="author">Author</option><option value="reviewer">Reviewer</option><option value="admin">Admin / Editor</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Affiliation" name="affiliation" value={form.affiliation} onChange={e => setForm(f => ({ ...f, affiliation: e.target.value }))} placeholder="University / Institute" testid="create-user-affiliation" />
            <FormField label="Designation" name="designation" value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))} placeholder="Professor, Editor, etc." testid="create-user-designation" />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#E5E7EB] text-[#374151] hover:bg-[#F5F7FA]">Cancel</button>
            <button type="submit" disabled={loading} className="flex items-center gap-1.5 px-5 py-2 text-sm bg-[#1A73E8] text-white font-medium hover:bg-[#174EA6] disabled:opacity-60">
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================
// JOURNAL CREATE/EDIT MODAL
// =============================================
const JournalModal = ({ token, journal, onClose, onSaved }) => {
  const isEdit = !!journal;
  const [form, setForm] = useState({
    title: journal?.title || '',
    shortTitle: journal?.shortTitle || '',
    description: journal?.description || '',
    aimsAndScope: journal?.aimsAndScope || '',
    field: journal?.field || '',
    frequency: journal?.frequency || 'Quarterly',
    issn: journal?.issn || 'Pending Assignment',
    eIssn: journal?.eIssn || 'Pending Assignment',
    impactFactor: journal?.impactFactor || 'Not Yet Rated',
    acceptanceRate: journal?.acceptanceRate || 'Under Review',
    submissionDeadline: journal?.submissionDeadline || 'Rolling submissions',
    reviewTime: journal?.reviewTime || '6-8 weeks',
    editorInChief: journal?.editorInChief || '',
    currentVolume: journal?.currentVolume || 1,
    currentIssue: journal?.currentIssue || 1,
    indexing: (journal?.indexing || []).join(', '),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedEditors, setSelectedEditors] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.shortTitle) { setError('Title and Short Title are required.'); return; }
    setLoading(true);
    const body = {
      ...form,
      indexing: form.indexing ? form.indexing.split(',').map(s => s.trim()).filter(Boolean) : [],
      assignedEditors: selectedEditors,
      currentVolume: parseInt(form.currentVolume) || 1,
      currentIssue: parseInt(form.currentIssue) || 1,
    };
    try {
      const url = isEdit ? `${BACKEND_URL}/api/journals/${journal.id}` : `${BACKEND_URL}/api/journals`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const fetchEditors = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/users`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        setUsers(data.filter(u => u.role === "admin"));
      } catch (err) { console.error(err); }
    };
    fetchEditors();
  }, [token]);

  useEffect(() => {
    if (journal) setSelectedEditors(journal.assignedEditors || []);
  }, [journal]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="journal-modal">
      <div className="bg-white border border-[#E5E7EB] w-full max-w-2xl my-8">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <h3 className="font-serif text-lg font-semibold text-[#1F1F1F]">{isEdit ? 'Edit Journal' : 'Add New Journal'}</h3>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151]"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={14} /> {error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Journal Title *" value={form.title} onChange={set('title')} placeholder="Full journal title" testid="journal-title" />
            <FormField label="Short Title *" value={form.shortTitle} onChange={set('shortTitle')} placeholder="e.g. JQCIS" testid="journal-short-title" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">Description</label>
            <textarea value={form.description} onChange={set('description')} rows={3} placeholder="Journal description..."
              className="w-full px-3 py-2 border border-[#E5E7EB] text-sm focus:ring-1 focus:ring-[#1A73E8] outline-none resize-none" data-testid="journal-description" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">Aims & Scope</label>
            <textarea value={form.aimsAndScope} onChange={set('aimsAndScope')} rows={3} placeholder="Aims and scope..."
              className="w-full px-3 py-2 border border-[#E5E7EB] text-sm focus:ring-1 focus:ring-[#1A73E8] outline-none resize-none" data-testid="journal-aims" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <FormField label="Field" value={form.field} onChange={set('field')} placeholder="e.g. Quantum Physics" testid="journal-field" />
            <FormField label="Frequency" value={form.frequency} onChange={set('frequency')} placeholder="Quarterly" testid="journal-frequency" />
            <FormField label="Editor-in-Chief" value={form.editorInChief} onChange={set('editorInChief')} placeholder="Name" testid="journal-editor" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <FormField label="ISSN" value={form.issn} onChange={set('issn')} placeholder="Pending" testid="journal-issn" />
            <FormField label="e-ISSN" value={form.eIssn} onChange={set('eIssn')} placeholder="Pending" testid="journal-eissn" />
            <FormField label="Impact Factor" value={form.impactFactor} onChange={set('impactFactor')} placeholder="Not Rated" testid="journal-if" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <FormField label="Review Time" value={form.reviewTime} onChange={set('reviewTime')} placeholder="6-8 weeks" />
            <FormField label="Volume" value={form.currentVolume} onChange={set('currentVolume')} type="number" />
            <FormField label="Issue" value={form.currentIssue} onChange={set('currentIssue')} type="number" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">Editorial Members</label>
            <div className="border border-[#E5E7EB] p-3 max-h-[150px] overflow-y-auto space-y-2">
              {users.length === 0 ? (
                <p className="text-xs text-[#9CA3AF]">No editors available. Create Admin users first.</p>
              ) : (
                users.map(u => (
                  <div key={u.id} className="flex items-center gap-2">
                    <input type="checkbox" checked={selectedEditors.includes(u.id)}
                      onChange={() => setSelectedEditors(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])} />
                    <span className="text-sm text-[#374151]">{u.name} ({u.designation || "Editor"})</span>
                  </div>
                ))
              )}
            </div>
          </div>
          <FormField label="Indexing (comma separated)" value={form.indexing} onChange={set('indexing')} placeholder="Google Scholar, Zenodo, DOAJ" testid="journal-indexing" />
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#E5E7EB] text-[#374151] hover:bg-[#F5F7FA]">Cancel</button>
            <button type="submit" disabled={loading} className="flex items-center gap-1.5 px-5 py-2 text-sm bg-[#1A73E8] text-white font-medium hover:bg-[#174EA6] disabled:opacity-60" data-testid="submit-journal">
              {loading ? 'Saving...' : <>{isEdit ? <Save size={14} /> : <Plus size={14} />} {isEdit ? 'Save Changes' : 'Create Journal'}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================
// SUBMISSION CREATE/EDIT MODAL
// =============================================
const SubmissionModal = ({ token, journals, submission, onClose, onSaved }) => {
  const isEdit = !!submission;
  const [form, setForm] = useState({
    title: submission?.title || '',
    abstract: submission?.abstract || '',
    keywords: submission?.keywords || '',
    journalId: submission?.journalId || '',
    journalTitle: submission?.journalTitle || '',
    authorName: submission?.authorName || '',
    authorEmail: submission?.authorEmail || '',
    status: submission?.status || 'submitted',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.authorName) { setError('Title and Author Name are required.'); return; }
    setLoading(true);
    try {
      const url = isEdit ? `${BACKEND_URL}/api/submissions/${submission.id}` : `${BACKEND_URL}/api/submissions`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const set = (field) => (e) => { setForm(f => ({ ...f, [field]: e.target.value })); setError(''); };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="submission-modal">
      <div className="bg-white border border-[#E5E7EB] w-full max-w-2xl">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <h3 className="font-serif text-lg font-semibold text-[#1F1F1F]">{isEdit ? 'Edit Research Paper' : 'Add New Research Paper'}</h3>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151]"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={14} /> {error}</div>}
          <FormField label="Paper Title *" value={form.title} onChange={set('title')} placeholder="Full title of the research paper" testid="paper-title" />
          <div>
            <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">Abstract</label>
            <textarea value={form.abstract} onChange={set('abstract')} rows={4} placeholder="Paper abstract..."
              className="w-full px-3 py-2 border border-[#E5E7EB] text-sm focus:ring-1 focus:ring-[#1A73E8] outline-none resize-none" data-testid="paper-abstract" />
          </div>
          <FormField label="Keywords (comma separated)" value={form.keywords} onChange={set('keywords')} placeholder="quantum, computing, algorithms" testid="paper-keywords" />
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Author Name *" value={form.authorName} onChange={set('authorName')} placeholder="Dr. Jane Smith" testid="paper-author" />
            <FormField label="Author Email" value={form.authorEmail} onChange={set('authorEmail')} type="email" placeholder="author@university.edu" testid="paper-email" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">Journal</label>
              <select value={form.journalTitle} onChange={e => {
                const j = journals.find(j => j.title === e.target.value);
                setForm(f => ({ ...f, journalTitle: e.target.value, journalId: j?.id || '' }));
              }} className="w-full px-3 py-2 border border-[#E5E7EB] text-sm bg-white focus:ring-1 focus:ring-[#1A73E8] outline-none" data-testid="paper-journal">
                <option value="">Select a journal</option>
                {journals.map(j => <option key={j.id} value={j.title}>{j.shortTitle} — {j.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">Status</label>
              <select value={form.status} onChange={set('status')}
                className="w-full px-3 py-2 border border-[#E5E7EB] text-sm bg-white focus:ring-1 focus:ring-[#1A73E8] outline-none" data-testid="paper-status">
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#E5E7EB] text-[#374151] hover:bg-[#F5F7FA]">Cancel</button>
            <button type="submit" disabled={loading} className="flex items-center gap-1.5 px-5 py-2 text-sm bg-[#1A73E8] text-white font-medium hover:bg-[#174EA6] disabled:opacity-60" data-testid="submit-paper">
              {loading ? 'Saving...' : <>{isEdit ? <Save size={14} /> : <Plus size={14} />} {isEdit ? 'Save Changes' : 'Add Paper'}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================
// GENERIC FORM FIELD
// =============================================
const FormField = ({ label, value, onChange, placeholder, type = 'text', testid, name }) => (
  <div>
    <label className="block text-xs font-semibold text-[#374151] uppercase tracking-wider mb-1.5">{label}</label>
    <input name={name} type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full px-3 py-2 border border-[#E5E7EB] text-sm text-[#1F1F1F] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8]"
      data-testid={testid} />
  </div>
);

// =============================================
// ASSIGN EDITORS MODAL
// =============================================
const AssignEditorsModal = ({ token, submission, editors, onClose, onSaved }) => {
  const [selectedIds, setSelectedIds] = useState(submission.assignedEditors || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleEditor = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 5) return prev;
      return [...prev, id];
    });
    setError('');
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) { setError('Select at least 1 editor.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/submissions/${submission.id}/assign-editors`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ editorIds: selectedIds }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="assign-editors-modal">
      <div className="bg-white border border-[#E5E7EB] w-full max-w-lg">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <h3 className="font-serif text-lg font-semibold text-[#1F1F1F]">Assign Editors</h3>
            <p className="text-xs text-[#9CA3AF] mt-0.5">Select up to 5 editors for: <strong>{submission.title}</strong></p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#374151]"><X size={18} /></button>
        </div>
        <div className="p-6">
          {error && <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={14} /> {error}</div>}
          {editors.length === 0 ? (
            <div className="text-center py-6 text-sm text-[#9CA3AF]">No editors available. Create Admin/Editor users first.</div>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {editors.map(editor => {
                const selected = selectedIds.includes(editor.id);
                return (
                  <div key={editor.id} onClick={() => toggleEditor(editor.id)}
                    className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${selected ? 'border-[#1A73E8] bg-blue-50' : 'border-[#E5E7EB] hover:bg-[#F9FAFB]'}`}
                    data-testid={`editor-option-${editor.id}`}>
                    <div className={`w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'bg-[#1A73E8] border-[#1A73E8]' : 'border-[#D1D5DB]'}`}>
                      {selected && <span className="text-white text-xs font-bold">✓</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1F1F1F]">{editor.name}</p>
                      <p className="text-xs text-[#6B7280]">{editor.designation || editor.email} · {editor.affiliation || ''}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-[#9CA3AF] mt-3">{selectedIds.length}/5 editors selected</p>
          <div className="flex gap-2 justify-end mt-4">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-[#E5E7EB] text-[#374151] hover:bg-[#F5F7FA]">Cancel</button>
            <button onClick={handleSubmit} disabled={loading || selectedIds.length === 0}
              className="flex items-center gap-1.5 px-5 py-2 text-sm bg-[#1A73E8] text-white font-medium hover:bg-[#174EA6] disabled:opacity-60"
              data-testid="confirm-assign-editors">
              {loading ? 'Assigning...' : `Assign ${selectedIds.length} Editor${selectedIds.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
